import React, { useEffect, useState, createElement } from 'react';
import { ChevronDown, ChevronUp, CheckCircle2, Clock, ShieldCheck, Download, Star, X, ArrowRight, Lock } from 'lucide-react';
import { ChatTestimonials } from './components/ChatTestimonials';
const PRODUCT_IMAGE = 'https://cdn.magicpatterns.com/uploads/uQ2pX92SnbTFbazyKmM5nx/Diseno_sin_titulo_(43).png';
const CHECKOUT_LINK = 'https://checkout.sectordigital.online/cart/44262193594411:1';
const AccordionItem = ({
  title,
  content
}: {
  title: string;
  content: React.ReactNode;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  return <div className="border border-slate-200 rounded-lg mb-3 overflow-hidden bg-white shadow-sm" data-id="element-0">
      <button className="w-full px-5 py-4 text-left flex justify-between items-center focus:outline-none hover:bg-slate-50 transition-colors" onClick={() => setIsOpen(!isOpen)} data-id="element-1">
        <span className="font-semibold text-slate-800 pr-4" data-id="element-2">{title}</span>
        {isOpen ? <ChevronUp className="text-teal-600 flex-shrink-0" size={20} data-id="element-3" /> : <ChevronDown className="text-slate-400 flex-shrink-0" size={20} data-id="element-4" />}
      </button>
      {isOpen && <div className="px-5 py-4 bg-slate-50 border-t border-slate-100 text-slate-600 leading-relaxed" data-id="element-5">
          {content}
        </div>}
    </div>;
};
const modules = [{
  title: 'Módulo 1 — ¿Qué es la neurodivergencia en consulta?',
  content: 'Una introducción práctica para entender cómo aparece la neurodivergencia en el trabajo clínico y cómo adaptar el encuadre desde el primer día.'
}, {
  title: 'Módulo 2 — TDAH en sesión',
  content: 'Herramientas para trabajar inatención, impulsividad, desorganización, procrastinación y desregulación emocional dentro del proceso terapéutico.'
}, {
  title: 'Módulo 3 — TEA en sesión',
  content: 'Estrategias para adaptar vínculo, lenguaje, previsibilidad, sensorialidad, comunicación y manejo de cambios sin infantilizar al paciente.'
}, {
  title: 'Módulo 4 — Altas capacidades en sesión',
  content: 'Recursos para intervenir frente a intensidad mental, perfeccionismo, aburrimiento, sobreexigencia, miedo al error y sensación de no encajar.'
}, {
  title: 'Módulo 5 — Dislexia y dificultades de aprendizaje',
  content: 'Una guía para acompañar el impacto emocional, la frustración escolar, la autoestima y la coordinación con familia o escuela cuando corresponda.'
}, {
  title: 'Módulo 6 — Evaluación y primera sesión',
  content: 'Protocolo práctico para saber qué observar, qué preguntar, qué evitar y cuándo derivar ante un posible perfil neurodivergente.'
}, {
  title: 'Módulo 7 — Intervención y técnicas adaptadas',
  content: 'Sistema para modificar técnicas cognitivas, conductuales, emocionales y tareas entre sesiones según el funcionamiento del paciente.'
}, {
  title: 'Módulo 8 — Trabajo con familia y entorno',
  content: 'Guía para psicoeducar, orientar apoyos, sostener límites y coordinar con familia, escuela, pareja o trabajo sin perder el foco terapéutico.'
}];
const faqs = [{
  title: '¿El pago es único?',
  content: 'Sí, el pago es único. No hay suscripciones ni cobros mensuales ocultos. Pagás una sola vez y tenés acceso al material.'
}, {
  title: '¿Cómo recibo el material?',
  content: <div data-id="element-6">
        <p className="font-semibold text-teal-700 mb-2" data-id="element-7">¡IMPORTANTE!</p>
        <p data-id="element-8">
          Al momento de la compra ingresás un mail. Una vez aprobado el pago,{' '}
          <strong data-id="element-9">el contenido te llega automáticamente a ese correo</strong>.
        </p>
        <p className="mt-2" data-id="element-10">
          Por favor, recordá revisar tu bandeja de SPAM o Promociones. Ante
          cualquier inconveniente podés comunicarte a{' '}
          <a href="mailto:sectordigital53@gmail.com" className="text-teal-600 underline" data-id="element-11">
            sectordigital53@gmail.com
          </a>{' '}
          y te ayudaremos a la brevedad.
        </p>
      </div>
}, {
  title: '¿Reemplaza una formación o supervisión profesional?',
  content: 'No. Este material es una guía clínica práctica para adaptar tus intervenciones, pero no reemplaza una formación de posgrado, evaluación formal ni supervisión profesional de casos.'
}, {
  title: '¿Es un curso?',
  content: 'No. Es un kit digital clínico-práctico con materiales descargables en formato texto/guía para usar como consulta y apoyo profesional rápido.'
}, {
  title: '¿Diagnostica TDAH, TEA, altas capacidades o dislexia?',
  content: 'No. El kit no reemplaza evaluaciones formales ni tests psicométricos. Su objetivo es ayudarte a adaptar el abordaje clínico una vez que hay sospecha o diagnóstico.'
}];
export function App() {
  const [timeLeft, setTimeLeft] = useState({
    hours: 0,
    minutes: 0,
    seconds: 0
  });
  const [showPopup, setShowPopup] = useState(false);
  const [popupData, setPopupData] = useState({
    name: '',
    city: ''
  });
  useEffect(() => {
    // Meta Pixel Injection
    const script = document.createElement('script');
    script.innerHTML = `
      !function(f,b,e,v,n,t,s)
      {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
      n.callMethod.apply(n,arguments):n.queue.push(arguments)};
      if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
      n.queue=[];t=b.createElement(e);t.async=!0;
      t.src=v;s=b.getElementsByTagName(e)[0];
      s.parentNode.insertBefore(t,s)}(window, document,'script',
      'https://connect.facebook.net/en_US/fbevents.js');
      fbq('init', '26257470350562748');
      fbq('track', 'PageView');
    `;
    document.head.appendChild(script);
    // Countdown Timer (to midnight)
    const timer = setInterval(() => {
      const now = new Date();
      const tomorrow = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
      const diff = tomorrow.getTime() - now.getTime();
      const hours = Math.floor(diff / (1000 * 60 * 60) % 24);
      const minutes = Math.floor(diff / 1000 / 60 % 60);
      const seconds = Math.floor(diff / 1000 % 60);
      setTimeLeft({
        hours,
        minutes,
        seconds
      });
    }, 1000);
    // Popup Logic
    const names = ['María', 'Laura', 'Sofía', 'Lucía', 'Florencia', 'Camila', 'Julieta', 'Martina', 'Carolina', 'Agustina'];
    const cities = ['Buenos Aires', 'Córdoba', 'Rosario', 'Mendoza', 'La Plata', 'Mar del Plata', 'Salta', 'Santa Fe', 'Tucumán', 'Neuquén'];
    const showRandomPopup = () => {
      const randomName = names[Math.floor(Math.random() * names.length)];
      const randomCity = cities[Math.floor(Math.random() * cities.length)];
      setPopupData({
        name: randomName,
        city: randomCity
      });
      setShowPopup(true);
      setTimeout(() => {
        setShowPopup(false);
      }, 5000);
    };
    const popupInterval = setInterval(() => {
      showRandomPopup();
    }, 25000); // Show every 25 seconds
    setTimeout(showRandomPopup, 5000); // Initial show after 5s
    return () => {
      clearInterval(timer);
      clearInterval(popupInterval);
    };
  }, []);
  const scrollToCheckout = (e: React.MouseEvent) => {
    e.preventDefault();
    document.getElementById('checkout-section')?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  const formatTime = (num: number) => num.toString().padStart(2, '0');
  return <div className="min-h-screen bg-slate-50 font-sans text-slate-800" data-id="element-12">
      {/* Hero Section */}
      <header className="relative bg-slate-900 text-white min-h-[100svh] flex flex-col justify-center px-4 py-8 text-center overflow-hidden" data-id="element-13">
        {/* Radial glow */}
        <div className="pointer-events-none absolute inset-0" style={{
        background: 'radial-gradient(ellipse 70% 55% at 50% 42%, rgba(20,184,166,0.18) 0%, rgba(15,23,42,0) 70%)'
      }} data-id="element-14" />
        <div className="relative max-w-3xl mx-auto w-full" data-id="element-15">
          <span className="inline-block border border-amber-400/40 text-amber-300 font-semibold tracking-[0.18em] uppercase text-[10px] sm:text-xs rounded-full px-4 py-1.5 mb-5 sm:mb-7" data-id="element-16">
            Kit Psicología en Neurodivergencias™
          </span>

          <h1 className="text-2xl sm:text-4xl md:text-5xl font-bold uppercase leading-[1.05] text-white mb-6 sm:mb-8 tracking-tight" data-id="element-17">
            Dejá de improvisar en tus acompañamientos en neurodivergencias
          </h1>

          <img src={PRODUCT_IMAGE} alt="Kit Psicología en Neurodivergencias" className="w-[58%] max-w-xs mx-auto mb-6 sm:mb-8 drop-shadow-2xl cursor-pointer hover:scale-105 transition-transform duration-300" onClick={scrollToCheckout} data-id="element-18" />

          <p className="text-sm sm:text-lg text-emerald-100/80 mb-7 sm:mb-9 max-w-2xl mx-auto font-light leading-relaxed" data-id="element-19">
            El sistema práctico para psicólogos que quieren observar el
            funcionamiento del paciente, adaptar el encuadre y elegir las
            técnicas más adecuadas según cada perfil
          </p>

          <button onClick={scrollToCheckout} className="group inline-flex items-center justify-center gap-2 bg-amber-400 hover:bg-amber-300 text-[#13402f] text-base sm:text-xl font-bold py-4 sm:py-5 px-8 sm:px-12 rounded-xl shadow-[0_10px_30px_rgba(251,191,36,0.3)] transform hover:-translate-y-0.5 transition-all duration-200 w-full sm:w-auto" data-id="element-20">
            SÍ, QUIERO EL KIT AHORA
            <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" data-id="element-21" />
          </button>

          <p className="flex items-center justify-center gap-2 mt-5 text-xs sm:text-sm text-emerald-100/70" data-id="element-22">
            <Lock size={14} data-id="element-23" /> Acceso inmediato · Pago único y seguro
          </p>
        </div>
      </header>

      {/* The System */}
      <section className="py-20 px-4 bg-white" data-id="element-24">
        <div className="max-w-4xl mx-auto" data-id="element-25">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-slate-900" data-id="element-26">
            Un sistema para adaptar la intervención clínica
          </h2>
          <p className="text-lg text-center mb-12 text-slate-600 max-w-2xl mx-auto" data-id="element-27">
            El kit está organizado para ayudarte a responder una pregunta
            central: <br data-id="element-28" />
            <br data-id="element-29" />
            <strong className="text-slate-800 text-xl" data-id="element-30">
              ¿Cómo intervengo cuando el paciente necesita una forma distinta de
              procesar, comunicarse, regularse o sostener el proceso
              terapéutico?
            </strong>
          </p>

          <div className="grid md:grid-cols-3 gap-8" data-id="element-31">
            <div className="bg-slate-50 p-8 rounded-2xl border border-slate-100 shadow-sm" data-id="element-32">
              <div className="bg-teal-100 w-14 h-14 rounded-full flex items-center justify-center mb-6" data-id="element-33">
                <span className="text-teal-700 font-bold text-xl" data-id="element-34">1</span>
              </div>
              <h3 className="text-xl font-bold mb-4" data-id="element-35">
                Comprender el funcionamiento
              </h3>
              <p className="text-slate-600" data-id="element-36">
                Para observar cómo atiende, comunica, aprende, se organiza, se
                regula y se vincula, sin reducirlo a una etiqueta diagnóstica.
              </p>
            </div>
            <div className="bg-slate-50 p-8 rounded-2xl border border-slate-100 shadow-sm" data-id="element-37">
              <div className="bg-teal-100 w-14 h-14 rounded-full flex items-center justify-center mb-6" data-id="element-38">
                <span className="text-teal-700 font-bold text-xl" data-id="element-39">2</span>
              </div>
              <h3 className="text-xl font-bold mb-4" data-id="element-40">Adaptar el encuadre</h3>
              <p className="text-slate-600" data-id="element-41">
                Para ajustar lenguaje, ritmo, consignas, tareas, estructura de
                sesión y forma de seguimiento según el perfil clínico.
              </p>
            </div>
            <div className="bg-slate-50 p-8 rounded-2xl border border-slate-100 shadow-sm" data-id="element-42">
              <div className="bg-teal-100 w-14 h-14 rounded-full flex items-center justify-center mb-6" data-id="element-43">
                <span className="text-teal-700 font-bold text-xl" data-id="element-44">3</span>
              </div>
              <h3 className="text-xl font-bold mb-4" data-id="element-45">
                Intervenir con herramientas
              </h3>
              <p className="text-slate-600" data-id="element-46">
                Para pasar de la comprensión del caso a técnicas, frases, tareas
                y estrategias aplicables en sesión.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Modules */}
      <section className="py-20 px-4 bg-slate-50" data-id="element-47">
        <div className="max-w-3xl mx-auto" data-id="element-48">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-slate-900" data-id="element-49">
            Qué incluye el Kit
          </h2>
          <div className="space-y-2" data-id="element-50">
            {modules.map((mod, idx) => <AccordionItem key={idx} title={mod.title} content={mod.content} data-id="element-51" />)}
          </div>
        </div>
      </section>

      {/* Bonuses */}
      <section className="py-20 px-4 bg-slate-900 text-white" data-id="element-52">
        <div className="max-w-4xl mx-auto" data-id="element-53">
          <div className="text-center mb-12" data-id="element-54">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-amber-400" data-id="element-55">
              TAMBIÉN INCLUYE 2 BONUS PRÁCTICOS
            </h2>
            <p className="text-xl text-slate-300 bg-amber-500/20 inline-block px-4 py-2 rounded-full border border-amber-500/30" data-id="element-56">
              🎁 Gratis por el día de hoy
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8" data-id="element-57">
            <div className="bg-slate-800 p-8 rounded-2xl border border-slate-700 relative overflow-hidden" data-id="element-58">
              <div className="absolute top-0 right-0 bg-amber-500 text-slate-900 font-bold py-1 px-4 rounded-bl-lg text-sm" data-id="element-59">
                BONUS #1
              </div>
              <h3 className="text-2xl font-bold mb-4 mt-4 text-white" data-id="element-60">
                Banco de Intervenciones Adaptadas por Perfil
              </h3>
              <p className="text-slate-300 mb-4" data-id="element-61">
                Un recurso clínico con herramientas listas para aplicar según el
                perfil neurodivergente del paciente y las dificultades que
                aparezcan en sesión.
              </p>
              <p className="text-slate-400 text-sm" data-id="element-62">
                Incluye intervenciones para TDAH, TEA, altas capacidades y
                dislexia/dificultades de aprendizaje, además de técnicas de
                regulación emocional, consignas, tareas y frases clínicas.
              </p>
            </div>

            <div className="bg-slate-800 p-8 rounded-2xl border border-slate-700 relative overflow-hidden" data-id="element-63">
              <div className="absolute top-0 right-0 bg-amber-500 text-slate-900 font-bold py-1 px-4 rounded-bl-lg text-sm" data-id="element-64">
                BONUS #2
              </div>
              <h3 className="text-2xl font-bold mb-4 mt-4 text-white" data-id="element-65">
                Guía para Psicoeducar a Familia y Entorno
              </h3>
              <p className="text-slate-300 mb-4" data-id="element-66">
                Una presentación práctica para explicar neurodivergencia sin
                patologizar, orientar apoyos, sostener límites y mejorar la
                coordinación con familia, escuela, pareja o trabajo.
              </p>
              <p className="text-slate-400 text-sm" data-id="element-67">
                Ideal para ordenar reuniones de orientación y ayudar al entorno
                a comprender mejor sin justificar cualquier conducta.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Target Audience */}
      <section className="py-20 px-4 bg-white" data-id="element-68">
        <div className="max-w-4xl mx-auto" data-id="element-69">
          <div className="flex flex-col md:flex-row items-center gap-12" data-id="element-70">
            <div className="md:w-1/2" data-id="element-71">
              <h2 className="text-3xl md:text-4xl font-bold mb-8 text-slate-900" data-id="element-72">
                Este kit es para vos si…
              </h2>
              <ul className="space-y-4" data-id="element-73">
                {['Atendés pacientes con TDAH, TEA, altas capacidades, dislexia o sospecha de perfiles neurodivergentes.', 'Sentís que algunas sesiones se vuelven difíciles de ordenar porque el paciente procesa, comunica o se regula de una forma distinta.', 'Necesitás adaptar consignas, tareas, lenguaje, ritmo de sesión y técnicas sin perder estructura clínica.', 'Querés intervenir con más claridad cuando aparecen inatención, rigidez, sobreexigencia, aburrimiento, sensorialidad o frustración escolar.', 'Buscás un material práctico, descargable y aplicable, sin convertir el abordaje en teoría extensa ni diagnóstico apresurado.'].map((item, idx) => <li key={idx} className="flex items-start" data-id="element-74">
                    <CheckCircle2 className="text-teal-500 mr-3 flex-shrink-0 mt-1" size={20} data-id="element-75" />
                    <span className="text-slate-700" data-id="element-76">{item}</span>
                  </li>)}
              </ul>
            </div>
            <div className="md:w-1/2 bg-slate-50 p-8 rounded-2xl border border-slate-200" data-id="element-77">
              <h3 className="text-2xl font-bold mb-6 text-slate-900" data-id="element-78">
                El Resultado
              </h3>
              <ul className="space-y-4" data-id="element-79">
                {['Con este kit vas a poder trabajar con mayor claridad clínica frente a pacientes neurodivergentes.', 'Vas a contar con una estructura para observar el funcionamiento del paciente, adaptar el encuadre, elegir herramientas más adecuadas y orientar al entorno cuando sea necesario.', 'Pasar de "entiendo que este paciente funciona distinto" a "sé cómo adaptar mi intervención para que el proceso terapéutico sea más claro, accesible y útil".'].map((item, idx) => <li key={idx} className="flex items-start" data-id="element-80">
                    <CheckCircle2 className="text-teal-500 mr-3 flex-shrink-0 mt-1" size={20} data-id="element-81" />
                    <span className="text-slate-700" data-id="element-82">{item}</span>
                  </li>)}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Offer Section (Checkout Target) */}
      <section id="checkout-section" className="min-h-[100svh] flex items-center justify-center py-6 px-4 bg-slate-100 border-t border-slate-200" data-id="element-83">
        <div className="max-w-md w-full mx-auto bg-white rounded-3xl shadow-xl overflow-hidden border border-slate-200" data-id="element-84">
          <div className="bg-slate-900 p-3 text-center" data-id="element-85">
            <h2 className="text-lg sm:text-xl font-bold text-white uppercase tracking-wide" data-id="element-86">
              Oferta Especial de Lanzamiento
            </h2>
          </div>

          <div className="p-5 sm:p-6" data-id="element-87">
            <div className="flex items-center gap-4 mb-4" data-id="element-88">
              <img src={PRODUCT_IMAGE} alt="Kit Psicología en Neurodivergencias" className="w-24 flex-shrink-0 drop-shadow-md" data-id="element-89" />
              <div data-id="element-90">
                <h3 className="text-base sm:text-lg font-bold text-slate-900 leading-tight" data-id="element-91">
                  KIT PSICOLOGÍA EN NEURODIVERGENCIAS™
                </h3>
                <p className="text-xs sm:text-sm text-slate-600 mt-1" data-id="element-92">
                  Sistema práctico para intervenir con mayor claridad en
                  pacientes neurodivergentes.
                </p>
              </div>
            </div>

            <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 mb-4" data-id="element-93">
              <h4 className="font-bold text-slate-800 mb-2 text-sm" data-id="element-94">
                Incluye:
              </h4>
              <ul className="space-y-1.5 text-xs sm:text-sm text-slate-700" data-id="element-95">
                <li data-id="element-96">
                  <CheckCircle2 className="inline text-teal-500 mr-2" size={15} data-id="element-97" />{' '}
                  Módulo principal completo sobre abordaje clínico
                </li>
                <li className="font-semibold text-amber-600" data-id="element-98">
                  <CheckCircle2 className="inline text-amber-500 mr-2" size={15} data-id="element-99" />{' '}
                  Bonus #1 — Banco de Intervenciones Adaptadas
                </li>
                <li className="font-semibold text-amber-600" data-id="element-100">
                  <CheckCircle2 className="inline text-amber-500 mr-2" size={15} data-id="element-101" />{' '}
                  Bonus #2 — Guía para Psicoeducar a Familia
                </li>
              </ul>
            </div>

            <div className="text-center mb-4" data-id="element-102">
              <p className="text-slate-500 line-through text-base mb-0.5" data-id="element-103">
                Precio Normal: $49.990 ARS
              </p>
              <div className="flex items-center justify-center gap-2" data-id="element-104">
                <span className="text-4xl font-extrabold text-slate-900" data-id="element-105">
                  $19.990
                </span>
                <span className="text-lg font-bold text-slate-900" data-id="element-106">ARS</span>
              </div>
            </div>

            <div className="bg-red-600 text-white p-3 rounded-xl text-center mb-4 shadow-inner" data-id="element-107">
              <p className="font-bold text-xs uppercase tracking-wider mb-1.5" data-id="element-108">
                OFERTA TERMINA EN:
              </p>
              <div className="flex justify-center gap-3 text-2xl font-mono font-bold" data-id="element-109">
                <div className="flex flex-col items-center" data-id="element-110">
                  <span data-id="element-111">{formatTime(timeLeft.hours)}</span>
                  <span className="text-[10px] font-sans font-normal opacity-80" data-id="element-112">
                    HORAS
                  </span>
                </div>
                <span data-id="element-113">:</span>
                <div className="flex flex-col items-center" data-id="element-114">
                  <span data-id="element-115">{formatTime(timeLeft.minutes)}</span>
                  <span className="text-[10px] font-sans font-normal opacity-80" data-id="element-116">
                    MIN
                  </span>
                </div>
                <span data-id="element-117">:</span>
                <div className="flex flex-col items-center" data-id="element-118">
                  <span data-id="element-119">{formatTime(timeLeft.seconds)}</span>
                  <span className="text-[10px] font-sans font-normal opacity-80" data-id="element-120">
                    SEG
                  </span>
                </div>
              </div>
            </div>

            <a href={CHECKOUT_LINK} onClick={() => {
            if (typeof (window as any).fbq === 'function') {
              ;
              (window as any).fbq('track', 'InitiateCheckout');
            }
          }} className="block w-full bg-teal-500 hover:bg-teal-400 text-white text-xl font-bold py-4 px-4 rounded-2xl text-center shadow-[0_10px_20px_rgba(20,184,166,0.3)] transform hover:-translate-y-1 transition-all duration-200" data-id="element-121">
              SÍ, QUIERO EL KIT AHORA
            </a>

            <div className="flex flex-wrap justify-center gap-3 mt-4 text-xs text-slate-500 font-medium" data-id="element-122">
              <span className="flex items-center" data-id="element-123">
                <ShieldCheck size={14} className="mr-1" data-id="element-124" /> Pago único
              </span>
              <span className="flex items-center" data-id="element-125">
                <Clock size={14} className="mr-1" data-id="element-126" /> Acceso inmediato
              </span>
              <span className="flex items-center" data-id="element-127">
                <Download size={14} className="mr-1" data-id="element-128" /> Material digital
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 px-4 bg-white" data-id="element-129">
        <div className="max-w-5xl mx-auto" data-id="element-130">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-slate-900" data-id="element-131">
            Lo que dicen los profesionales
          </h2>

          <ChatTestimonials data-id="element-132" />

          <div className="grid md:grid-cols-2 gap-6" data-id="element-133">
            <div className="bg-slate-50 p-6 rounded-xl border border-slate-200" data-id="element-134">
              <div className="flex text-amber-400 mb-3" data-id="element-135">
                <Star fill="currentColor" size={20} data-id="element-136" />
                <Star fill="currentColor" size={20} data-id="element-137" />
                <Star fill="currentColor" size={20} data-id="element-138" />
                <Star fill="currentColor" size={20} data-id="element-139" />
                <Star fill="currentColor" size={20} data-id="element-140" />
              </div>
              <p className="text-slate-700 italic mb-4" data-id="element-141">
                "Excelente material. Me sirvió muchísimo para ordenar las
                sesiones con mis pacientes con TDAH. Las herramientas son súper
                prácticas y fáciles de aplicar en el consultorio. ¡Lo
                recomiendo!"
              </p>
              <p className="font-bold text-slate-900" data-id="element-142">
                — Lic. Martina R., Psicóloga Clínica (CABA)
              </p>
            </div>
            <div className="bg-slate-50 p-6 rounded-xl border border-slate-200" data-id="element-143">
              <div className="flex text-amber-400 mb-3" data-id="element-144">
                <Star fill="currentColor" size={20} data-id="element-145" />
                <Star fill="currentColor" size={20} data-id="element-146" />
                <Star fill="currentColor" size={20} data-id="element-147" />
                <Star fill="currentColor" size={20} data-id="element-148" />
                <Star fill="currentColor" size={20} data-id="element-149" />
              </div>
              <p className="text-slate-700 italic mb-4" data-id="element-150">
                "Buscaba algo que no fuera pura teoría y este kit es exactamente
                eso. Las guías para psicoeducar a los padres me resolvieron un
                montón de dudas sobre cómo transmitir la información sin
                patologizar."
              </p>
              <p className="font-bold text-slate-900" data-id="element-151">
                — Lic. Julieta M., Psicóloga (Córdoba)
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-20 px-4 bg-slate-50 border-t border-slate-200" data-id="element-152">
        <div className="max-w-3xl mx-auto" data-id="element-153">
          <h2 className="text-3xl font-bold text-center mb-12 text-slate-900" data-id="element-154">
            Preguntas Frecuentes
          </h2>
          <div className="space-y-2" data-id="element-155">
            {faqs.map((faq, idx) => <AccordionItem key={idx} title={faq.title} content={faq.content} data-id="element-156" />)}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-slate-400 py-12 px-4 text-center text-sm" data-id="element-157">
        <div className="max-w-4xl mx-auto" data-id="element-158">
          <p className="mb-6" data-id="element-159">
            Este material está dirigido a profesionales de la salud mental.
          </p>
          <p className="text-xs opacity-60" data-id="element-160">
            El sitio no forma parte de Facebook, Instagram ni Meta Platforms
            Inc.
          </p>
          <p className="text-xs opacity-60 mt-4" data-id="element-161">
            © {new Date().getFullYear()} Sector Digital. Todos los derechos
            reservados.
          </p>
        </div>
      </footer>

      {/* Popup Notification */}
      <div className={`fixed bottom-6 left-6 max-w-sm bg-white rounded-lg shadow-2xl border border-slate-100 overflow-hidden transition-all duration-500 transform ${showPopup ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0 pointer-events-none'} z-50`} data-id="element-162">
        <div className="bg-green-500 text-white text-xs font-bold px-3 py-1 flex items-center" data-id="element-163">
          <CheckCircle2 size={12} className="mr-1" data-id="element-164" /> COMPRA VERIFICADA
        </div>
        <div className="p-4 flex items-start" data-id="element-165">
          <div className="bg-slate-100 rounded-full p-2 mr-3" data-id="element-166">
            <Download size={20} className="text-teal-600" data-id="element-167" />
          </div>
          <div data-id="element-168">
            <p className="text-sm text-slate-800" data-id="element-169">
              <span className="font-bold" data-id="element-170">{popupData.name}</span> de{' '}
              {popupData.city}
            </p>
            <p className="text-xs text-slate-500 mt-1" data-id="element-171">
              Acaba de comprar el Kit Psicología en Neurodivergencias
            </p>
          </div>
          <button onClick={() => setShowPopup(false)} className="ml-auto text-slate-400 hover:text-slate-600" data-id="element-172">
            <X size={16} data-id="element-173" />
          </button>
        </div>
      </div>
    </div>;
}